import { Component, inject } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, convertToParamMap, ParamMap, Params, RouterModule } from "@angular/router";
import * as L from "leaflet";
import { webatlasTileLayer } from "leaflet-webatlastile";
import { PolyDrawHelpers, PolyDrawService, PolygonEventType, PolygonInformationService, PolygonState } from "polydraw";
import { combineLatest, debounceTime, distinctUntilChanged, filter, forkJoin, of, Subscription, switchMap, take } from "rxjs";
import { BasePageComponent } from "src/app/base-page/base-page.component";
import { environment } from "src/environments/environment";
import { AreaBuilderDataService } from "src/services/area-builder-data.service";
import { FavoriteListDataService } from "src/services/estate/favorite-list-data.service";
import { InternalNavigationService } from "src/services/internal-navigation.service";
import { MapActionsService } from "src/services/map/map-actions.service";
import { MapMode, MapStateService } from "src/services/map/map-state.service";
import { RightSidebarOptions, RightSidebarService } from "src/services/right-sidebar.service";
import { AreasStore } from "src/stores/area-builder.store";
import { AreaBuilderUtils } from "src/util/map/areabuilder.util";
import { PolygonStorageUtil } from "src/util/map/polygon.storage.util";
import { MapModeButtonsComponent } from "./map-mode-buttons/map-mode-buttons.component";
import { MapNavigationToolsComponent } from "./map-navigation-tools/map-navigation-tools.component";

@Component({
    selector: "app-ev-map",
    templateUrl: "./ev-map.component.html",
    styleUrl: "./ev-map.component.scss",
    imports: [RouterModule, MapModeButtonsComponent, MapNavigationToolsComponent, BasePageComponent],
})
export class EvMapComponent {
    private readonly mapActionsService = inject(MapActionsService);
    private readonly mapStateService = inject(MapStateService);
    private readonly route = inject(ActivatedRoute);
    private readonly polygonInfoService = inject(PolygonInformationService);
    private readonly navigationService = inject(InternalNavigationService);
    private readonly areaBuilderService = inject(AreaBuilderDataService);
    private readonly areaBuilderUtils = inject(AreaBuilderUtils);
    readonly areasStore = inject(AreasStore);
    private readonly polydrawService = inject(PolyDrawService);
    private readonly rightSidebarService = inject(RightSidebarService);
    private readonly favoriteListDataService = inject(FavoriteListDataService);

    protected selectAnyEstateActive = toSignal(this.mapStateService.mapPointerActive$);
    private subscriptions: Subscription[] = [];

    private lat = 59.923535708819344;
    private lon = 10.746667385101318;
    private zoom = 17;
    private map: L.Map;
    storageId = -1;
    justUpdated = false;

    constructor() {}

    private selectedAreaBoundaries$ = toObservable(this.areasStore.selectedAreas).pipe(
        switchMap((areas) => {
            if (!areas?.length) {
                return of([]);
            }
            return forkJoin(areas.map((area) => this.areaBuilderService.getAreaBoundaryCached(area)));
        }),
    );

    drawableAreas = this.selectedAreaBoundaries$
        .pipe(debounceTime(100))
        .subscribe((boundaries) => {
            // Capture any free-drawn polygons currently on the map (read live, not from a store)
            // so they survive the redraw below.
            const { selfDrawnPolygons } = this.areaBuilderService.setAreasAndPolygons();
            const drawnGroups = this.areaBuilderUtils.getFeatureGroups(selfDrawnPolygons, []);

            // addAllPolygonsWithMetaData is append-only, so clear everything first to drop removed
            // areas (and avoid duplicates), then re-add the remaining areas together with the drawn polygons.
            this.polydrawService.removeAllFeatureGroups();
            this.polydrawService.addAllPolygonsWithMetaData([...boundaries, ...drawnGroups]);

            if (boundaries.length && this.map) {
                const polyHelper = new PolyDrawHelpers(this.polygonInfoService);
                const bBox = polyHelper.getCombinedBoundingBox();
                this.map.fitBounds(bBox, { paddingTopLeft: [50, 10], paddingBottomRight: [10, 50] });
            }
        });

    public ngOnInit(): void {
        const lat = this.route.snapshot.queryParams["lat"];
        if (lat) this.lat = lat;
        const lon = this.route.snapshot.queryParams["lon"];
        if (lon) this.lon = lon;
        const zoom = this.route.snapshot.queryParams["zoom"];
        if (zoom) this.zoom = zoom;
        let useUserPosition = true; 
        const estateID = this.route.snapshot.queryParams["estateID"] ?? this.route.snapshot.queryParams["estateId"];
        const unitID = this.route.snapshot.queryParams["unitID"] ?? this.route.snapshot.queryParams["unitId"];
        if(estateID || unitID) useUserPosition = false;
        this.initMap(useUserPosition);

        this.subscriptions.push(
            this.polygonInfoService.polygonState$
                //debounceTime filterer vekk delete-meldinger i forbindelse med oppretting av "automatisk genererte" områder, så den må være med
                .pipe(
                    debounceTime(100),
                    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                )
                .subscribe((state: PolygonState) => {
                    this.updateUrl(state);
                }),
            combineLatest([this.mapStateService.estateListEditMode$, this.polygonInfoService.polygonState$.pipe(debounceTime(100))])
                .pipe(filter(([favouriteListEditMode, state]) => !favouriteListEditMode || !state.isAutoGenerated))
                .subscribe({
                    next: ([, state]) => {
                        // Check to see if all areas have been deleted
                        // this.handleAreas(state);
                        this.handlePolygonInSession(state);
                    },
                }),
            this.route.queryParams.pipe(take(1)).subscribe((p: Params) => {
                this.onParamChanged(p);
            }),
        );
    }

    initMap(useUserPosition: boolean): void {
        const leafletOptions: L.MapOptions = {
            zoomControl: false,
            zoom: this.zoom,
            minZoom: 5,
            maxZoom: 20,
            zoomDelta: 1,
            zoomSnap: 1,
            maxBounds: [
                [90, -180],
                [-90, 180],
            ],
        };
        const map = new L.Map("evmap", leafletOptions);
        map.setView([this.lat, this.lon], this.zoom);

        webatlasTileLayer({ apiKey: "0420312a-121d-4139-bd48-16ca91e9866b" }).addTo(map);

        this.map = map;
        this.mapActionsService.updateUseLocation(useUserPosition);
        this.mapActionsService.onMapReady(this.map);
        this.mapActionsService.earlyUpdateMapMode();
    }

    openWeb40(mapMode: MapMode): void {
        let area = "";
        switch (mapMode) {
            case MapMode.Properties:
                area = "estates";
                break;
            case MapMode.NewDwellings:
                area = "projects";
                break;
            default:
                "market";
        }
        const latLon = this.map.getCenter();
        const zoom = this.map.getZoom();
        const params = this.route.snapshot.queryParams;
        let url = `${environment.evwebUrl}/search/${area}?reloadmap=true&lon=${latLon.lng}&lat=${latLon.lat}&zoom=${zoom}`;
        if (params["areaId"] && params["areaTypeId"]) {
            url += `&areaId=${params["areaId"]}&areaTypeId=${params["areaTypeId"]}`;
        }
        window.open(url, "_self");
    }
    
    //TODO - flytte områdevelger til samme kart
    // private handleAreas(state: PolygonState) {
    //     if (state.polygonEventType === PolygonEventType.Modify && state?.metaData?.length) {
    //         const mapData = this.areaBuilderService.getAreasAndPolygons();
    //         if (!mapData.areas.length) {
    //             this.areaBuilderService.clearAreaBuilder();
    //         }
    //     }
    // }

    private handlePolygonInSession(state: PolygonState) {
        if (state.polygonEventType !== PolygonEventType.Delete && state?.polygon?.length > 0 && !state.isAutoGenerated) {
            this.storePolygonsInSession();
        } else {
            this.storageId = -1;
            this.areaBuilderService.setAreasAndPolygons();
        }

        if (!state.isAutoGenerated) {
            this.syncSelectedAreasWithMap();
        }
    }

    // Removes from the AreasStore any selected area whose polygon is no longer present on the map.
    private syncSelectedAreasWithMap(): void {
        const areasOnMap = this.areaBuilderService.getAreasAndPolygons().areas;
        const removedAreas = this.areasStore
            .selectedAreas()
            .filter((selected) => !areasOnMap.some((a) => a.areaId === selected.areaId && (a.areaType as number) === selected.areaType));
        removedAreas.forEach((area) => this.areasStore.removeSelectedArea(area, area.parentAreaKey));
    }

    private storePolygonsInSession() {
        const mapData = this.areaBuilderService.setAreasAndPolygons();
        const polys = mapData.selfDrawnPolygons;

        if (polys === null) return;
        if (this.storageId < 0) {
            this.storageId = PolygonStorageUtil.add(polys);
            this.justUpdated = true;
            this.navigationService.updateUrlParams({ poly: this.storageId });
        } else {
            PolygonStorageUtil.update(this.storageId, polys);
        }
    }

    private updateUrl(state: PolygonState): void {
        if (state.polygonEventType === PolygonEventType.Delete && (state?.polygon?.length ?? 0 === 0)) {
            this.navigationService.updateUrlParams({ areadId: null, areaTypeId: null, poly: null });
        }
    }

    private onParamChanged(p: Params) {
        const paramMap: ParamMap = convertToParamMap(p);
        const hasSetAreaBuilderId = paramMap.has("areabuilderid") && paramMap.get("areabuilderid") !== "-1";

        if (paramMap.has("poly")) {
            this.storageId = Number(paramMap.get("poly"));
            const polys = PolygonStorageUtil.get(this.storageId);
            // if (!this.justUpdated) {
            //     this.drawPolygonsAndZoomAround(polys);
            // }
            this.justUpdated = false;
        }
        if (paramMap.has("favoritelist") && !hasSetAreaBuilderId) {
            this.areaBuilderService.setAreasAndPolygons(true)
            this.areasStore.removeAllSelectedAreas();
            this.openFavoriteList(Number(paramMap.get("favoritelist")), false);
        }
    }

    private openFavoriteList(favoriteListId: number, hasNewAreas: boolean) {
        this.rightSidebarService.toggleSidebarVisibility(RightSidebarOptions.Favorites);
        return this.favoriteListDataService.loadFavoriteList(favoriteListId, false, hasNewAreas);
    }
}
